import React, {FC, ReactElement} from "react";
import {Popconfirm} from "antd";
import type {PopconfirmProps} from "antd";
import type {TFunction} from "i18next";
import {useTranslation} from "react-i18next";

export type DeleteConfirmationTarget = {
    itemType: string;
    itemName?: string | number | null;
    count?: number;
    warning?: string | null;
};
export type DeleteConfirmationCopy = {
    title: string;
    description: string;
    okText: string;
    cancelText: string;
};

export const buildDeleteConfirmation = (
    t: TFunction,
    {itemType, itemName, count, warning}: DeleteConfirmationTarget
): DeleteConfirmationCopy => {
    const normalizedItemName = itemName == null ? "" : String(itemName).trim();
    const targetDescription = count != null
        ? t("delete-confirmation-count-target", {
            count,
            itemType,
            defaultValue: "Selected records to delete ({{itemType}}): {{count}}."
        })
        : normalizedItemName
            ? t("delete-confirmation-named-target", {
                itemName: normalizedItemName,
                itemType,
                defaultValue: "The {{itemType}} \"{{itemName}}\" will be deleted."
            })
            : t("delete-confirmation-target", {
                itemType,
                defaultValue: "The {{itemType}} will be deleted."
            });
    const question = t("delete-confirmation-question", {
        defaultValue: "Are you sure you want to delete it?"
    });

    return {
        title: t("delete-confirmation-title", {defaultValue: "Confirm deletion"}),
        description: [targetDescription, warning?.trim(), question].filter(Boolean).join(" "),
        okText: t("delete", {defaultValue: "Delete"}),
        cancelText: t("cancel", {defaultValue: "Cancel"})
    };
};

type DeletePopconfirmProps = Omit<
    PopconfirmProps,
    "cancelText" | "description" | "okText" | "title"
> & DeleteConfirmationTarget & {
    children: ReactElement;
};

export const DeletePopconfirm: FC<DeletePopconfirmProps> = ({
    children,
    count,
    itemName,
    itemType,
    okButtonProps,
    warning,
    ...popconfirmProps
}) => {
    const {t} = useTranslation();
    const confirmation = buildDeleteConfirmation(t, {
        count,
        itemName,
        itemType,
        warning
    });

    return (
        <Popconfirm
            {...popconfirmProps}
            title={confirmation.title}
            description={confirmation.description}
            okText={confirmation.okText}
            cancelText={confirmation.cancelText}
            okButtonProps={{...okButtonProps, danger: true}}
        >
            {children}
        </Popconfirm>
    );
};
